import { useCallback, useEffect, useRef, useState } from 'react';
import { BarcodeDetector as PolyfillDetector, setZXingModuleOverrides } from 'barcode-detector/pure';

/**
 * Розпізнавання штрих-кодів із камери.
 *
 * Чому не react-qr-barcode-scanner (було до цього):
 *  1. Кожні 500 мс він робив JPEG-знімок кадру (`getScreenshot()`), створював
 *     з нього <img>, чекав завантаження і тільки тоді декодував. JPEG-стиснення
 *     «замилює» тонкі штрихи — саме те, з чого складається EAN-13.
 *  2. На кожен кадр створювався НОВИЙ `BrowserMultiFormatReader`.
 *  3. Два кадри на секунду: касир мусив тримати телефон нерухомо кілька секунд.
 *  4. Декодувався весь кадр цілком, хоча штрих-код — у рамці посередині.
 *  5. Його `setInterval` мав порожній масив залежностей і назавжди тримав
 *     найперший `onUpdate`. Через це наш захист від повторів читав застаріле
 *     значення `scanLocked` і не працював — один штрих-код міг додати товар
 *     кілька разів поспіль.
 *
 * Як тепер:
 *  • кадр беремо прямо з <video> у canvas, без JPEG;
 *  • декодуємо лише центральну рамку (ROI), зменшену до робочої ширини —
 *    менше пікселів, більше кадрів на секунду й менше хибних спрацювань;
 *  • декодер створюється один раз;
 *  • ~10 спроб на секунду, наступна не стартує, поки не завершилась попередня;
 *  • список форматів обмежений роздрібними — ZXing не витрачає час на решту;
 *  • на пристроях з вбудованим BarcodeDetector (Android Chrome) працює він,
 *    на iOS Safari — WASM-декодер із власного домену (без CDN).
 */

// Декодер-поліфіл вантажить .wasm з fastly.jsdelivr.net. Каса має працювати
// без інтернету, тому файл лежить у public/ (scripts/copy-scanner-wasm.js).
setZXingModuleOverrides({
    locateFile: (file, prefix) => {
        if (file.endsWith('.wasm')) {
            return `${process.env.PUBLIC_URL || ''}/${file}`;
        }

        return prefix + file;
    },
});

/** Формати, які реально трапляються на товарних етикетках. */
export const BARCODE_FORMATS = ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128', 'code_39', 'itf'];

/** Скільки разів на секунду пробуємо розпізнати. */
const SCAN_INTERVAL_MS = 100;

/**
 * Ширина, до якої зменшуємо вирізану смугу перед декодуванням.
 *
 * Підібрано експериментально на згенерованих штрих-кодах: при 640 px
 * код, що займає 22% ширини кадру, не розпізнавався взагалі (після
 * зменшення на модуль лишалося менше двох пікселів), при 960 px —
 * розпізнається за ~0.5 с, а 1280 px не додає нічого, крім навантаження.
 */
const WORK_WIDTH = 960;

/**
 * Висота смуги наведення — частка ЕКРАНА (див. .rc-scanner__frame).
 * По вертикалі декодуємо саме те, що бачить касир.
 */
export const ROI = { height: 0.4 };

/**
 * По горизонталі беремо майже весь кадр камери, а не лише видиму частину.
 *
 * Відео показується з object-fit: cover, і на портретному телефоні видно
 * тільки центральну смугу ландшафтного кадру (~26% ширини на 390px екрані).
 * Штрих-код — широкий об'єкт: якби ми декодували строго видиме, касир мусив
 * би відсувати телефон, поки код не стане надто дрібним для розпізнавання.
 * Тому по горизонталі декодер бачить більше, ніж екран, — код зчитується
 * навіть тоді, коли візуально «не влазить» у кадр.
 */
const HORIZONTAL_COVERAGE = 0.96;

/** Невеликий запас по вертикалі, щоб пробачати неточне наведення. */
const ROI_TOLERANCE = 1.15;

/** Той самий код не приймаємо повторно протягом цього часу. */
const SAME_CODE_COOLDOWN_MS = 2500;

export const SCANNER_ERRORS = {
    insecure: {
        title: 'Камера недоступна',
        detail: 'Браузер дає доступ до камери лише на HTTPS. Відкрийте застосунок за захищеною адресою.',
    },
    unsupported: {
        title: 'Браузер не підтримує камеру',
        detail: 'Оновіть браузер або скористайтеся пошуком товару за назвою.',
    },
    denied: {
        title: 'Доступ до камери заборонено',
        detail: 'Дозвольте доступ у налаштуваннях браузера для цього сайту й спробуйте ще раз.',
    },
    notFound: {
        title: 'Камеру не знайдено',
        detail: 'До пристрою не підключено жодної камери.',
    },
    busy: {
        title: 'Камера зайнята',
        detail: 'Її використовує інший застосунок. Закрийте його й спробуйте ще раз.',
    },
    unknown: {
        title: 'Не вдалося увімкнути камеру',
        detail: 'Спробуйте ще раз або перезавантажте сторінку.',
    },
};

const mapCameraError = (error) => {
    switch (error?.name) {
        case 'NotAllowedError':
        case 'SecurityError':
            return 'denied';
        case 'NotFoundError':
        case 'DevicesNotFoundError':
        case 'OverconstrainedError':
            return 'notFound';
        case 'NotReadableError':
        case 'TrackStartError':
            return 'busy';
        default:
            return 'unknown';
    }
};

/**
 * @param {object}   options
 * @param {boolean}  options.active   чи має камера бути увімкненою
 * @param {Function} options.onDetect викликається з розпізнаним рядком коду
 */
const useBarcodeScanner = ({ active, onDetect }) => {
    const videoRef = useRef(null);
    const streamRef = useRef(null);
    const detectorRef = useRef(null);
    const engineRef = useRef(null);
    const canvasRef = useRef(null);
    const timerRef = useRef(null);
    const busyRef = useRef(false);
    const lastCodeRef = useRef({ value: null, at: 0 });
    // onDetect у ref, щоб цикл розпізнавання не перезапускався на кожен
    // рендер батьківського компонента (і не тримав застарілий обробник).
    const onDetectRef = useRef(onDetect);

    const [status, setStatus] = useState('idle'); // idle | starting | scanning | error
    const [errorKind, setErrorKind] = useState(null);
    const [torch, setTorch] = useState({ supported: false, on: false });

    useEffect(() => {
        onDetectRef.current = onDetect;
    }, [onDetect]);

    /**
     * Вибір рушія розпізнавання.
     *
     * Вбудований BarcodeDetector (Android Chrome, ChromeOS, macOS) працює
     * на рівні ОС і швидший за WASM, тож маємо йому віддавати перевагу.
     * Але сама наявність `window.BarcodeDetector` нічого не гарантує:
     * на Android він спирається на ML Kit з Google Play Services, і на
     * пристроях без GMS (частина Huawei, «чисті» AOSP-прошивки) об'єкт
     * існує, а підтримуваних форматів у нього нема — сканер тоді мовчки
     * не спрацьовував би ніколи. Тому питаємо getSupportedFormats() і
     * відкочуємось на WASM, якщо потрібних форматів немає.
     */
    const getDetector = useCallback(async () => {
        if (detectorRef.current) {
            return detectorRef.current;
        }

        const Native = typeof window !== 'undefined' ? window.BarcodeDetector : null;

        if (Native?.getSupportedFormats) {
            try {
                const supported = await Native.getSupportedFormats();
                const usable = BARCODE_FORMATS.filter((format) => supported.includes(format));

                if (usable.includes('ean_13')) {
                    detectorRef.current = new Native({ formats: usable });
                    engineRef.current = 'native';
                    return detectorRef.current;
                }

                console.info(
                    'Вбудований BarcodeDetector не підтримує потрібні формати — переходимо на WASM',
                    supported
                );
            } catch (error) {
                console.info('Вбудований BarcodeDetector недоступний — переходимо на WASM', error);
            }
        }

        detectorRef.current = new PolyfillDetector({ formats: BARCODE_FORMATS });
        engineRef.current = 'wasm';
        return detectorRef.current;
    }, []);

    const stop = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }

        const stream = streamRef.current;

        if (stream) {
            stream.getTracks().forEach((track) => track.stop());
            streamRef.current = null;
        }

        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }

        busyRef.current = false;
        setTorch({ supported: false, on: false });
    }, []);

    const toggleTorch = useCallback(async () => {
        const track = streamRef.current?.getVideoTracks?.()[0];

        if (!track) {
            return;
        }

        const next = !torch.on;

        try {
            await track.applyConstraints({ advanced: [{ torch: next }] });
            setTorch((current) => ({ ...current, on: next }));
        } catch (error) {
            console.warn('Ліхтарик недоступний', error);
            setTorch((current) => ({ ...current, supported: false }));
        }
    }, [torch.on]);

    useEffect(() => {
        if (!active) {
            stop();
            setStatus('idle');
            setErrorKind(null);
            return undefined;
        }

        let cancelled = false;

        const fail = (kind) => {
            if (!cancelled) {
                setErrorKind(kind);
                setStatus('error');
            }
        };

        const scanFrame = async () => {
            const video = videoRef.current;

            if (cancelled || !video || video.readyState < 2 || busyRef.current) {
                return;
            }

            busyRef.current = true;

            try {
                const { videoWidth: vw, videoHeight: vh } = video;

                if (!vw || !vh) {
                    return;
                }

                // Рамку з екрана переводимо в координати кадру камери.
                // Відео показується з object-fit: cover, тож на портретному
                // телефоні видно лише центральну смугу ландшафтного кадру —
                // без цього перерахунку декодер читав би зовсім не те, на що
                // касир наводить рамку.
                const rect = video.getBoundingClientRect();
                const viewW = rect.width || vw;
                const viewH = rect.height || vh;
                const cover = Math.max(viewW / vw, viewH / vh);
                const offsetY = (vh * cover - viewH) / 2;

                const frameH = viewH * ROI.height * ROI_TOLERANCE;

                const cropW = Math.round(vw * HORIZONTAL_COVERAGE);
                const cropH = Math.round(Math.min(vh, frameH / cover));
                const cropX = Math.round((vw - cropW) / 2);
                const cropY = Math.round(
                    Math.max(0, Math.min(vh - cropH, (offsetY + (viewH - frameH) / 2) / cover))
                );

                if (cropW < 2 || cropH < 2) {
                    return;
                }

                const scale = Math.min(1, WORK_WIDTH / cropW);
                const outW = Math.round(cropW * scale);
                const outH = Math.round(cropH * scale);

                if (!canvasRef.current) {
                    canvasRef.current = document.createElement('canvas');
                }

                const canvas = canvasRef.current;

                if (canvas.width !== outW || canvas.height !== outH) {
                    canvas.width = outW;
                    canvas.height = outH;
                }

                const context = canvas.getContext('2d', { willReadFrequently: true });
                context.drawImage(video, cropX, cropY, cropW, cropH, 0, 0, outW, outH);

                const detector = await getDetector();
                const results = await detector.detect(canvas);
                const value = results?.[0]?.rawValue?.trim();

                if (!value || value.length < 6) {
                    return;
                }

                // Той самий код поспіль ігноруємо: касир тримає товар перед
                // камерою ще секунду після сигналу, і без цього один штрих-код
                // додавався б кілька разів.
                const now = Date.now();
                const last = lastCodeRef.current;

                if (last.value === value && now - last.at < SAME_CODE_COOLDOWN_MS) {
                    return;
                }

                lastCodeRef.current = { value, at: now };
                onDetectRef.current?.(value);
            } catch (error) {
                // Кадр без коду — звичайна ситуація, не помилка.
                if (error?.name !== 'NotFoundException') {
                    console.debug('Кадр не розпізнано', error);
                }
            } finally {
                busyRef.current = false;
            }
        };

        const loop = async () => {
            await scanFrame();

            if (!cancelled) {
                timerRef.current = setTimeout(loop, SCAN_INTERVAL_MS);
            }
        };

        const start = async () => {
            setStatus('starting');
            setErrorKind(null);

            if (typeof window !== 'undefined' && window.isSecureContext === false) {
                fail('insecure');
                return;
            }

            if (!navigator.mediaDevices?.getUserMedia) {
                fail('unsupported');
                return;
            }

            try {
                const stream = await navigator.mediaDevices.getUserMedia({
                    audio: false,
                    video: {
                        // ideal, а не exact: на ноутбуці задньої камери немає,
                        // і з exact запит просто впав би.
                        facingMode: { ideal: 'environment' },
                        width: { ideal: 1280 },
                        height: { ideal: 720 },
                    },
                });

                if (cancelled) {
                    stream.getTracks().forEach((track) => track.stop());
                    return;
                }

                streamRef.current = stream;

                const video = videoRef.current;

                if (!video) {
                    stream.getTracks().forEach((track) => track.stop());
                    return;
                }

                video.srcObject = stream;
                // playsInline обов'язковий для iOS Safari — без нього відео
                // відкривається на весь екран у власному плеєрі.
                video.playsInline = true;
                video.muted = true;

                await video.play().catch(() => {});

                const track = stream.getVideoTracks()[0];
                const capabilities = track?.getCapabilities?.() ?? {};
                setTorch({ supported: Boolean(capabilities.torch), on: false });

                // Прогріваємо декодер, поки касир наводить камеру: перший
                // виклик тягне й компілює WASM (~1 с), і без прогріву ця
                // пауза припала б саме на перший штрих-код.
                getDetector()
                    .then((detector) => detector.detect(new ImageData(8, 8)))
                    .catch(() => {});

                if (!cancelled) {
                    setStatus('scanning');
                    loop();
                }
            } catch (error) {
                console.error('Не вдалося увімкнути камеру', error);
                fail(mapCameraError(error));
            }
        };

        start();

        return () => {
            cancelled = true;
            stop();
        };
    }, [active, getDetector, stop]);

    return {
        videoRef,
        /** 'native' | 'wasm' — яким рушієм розпізнаємо (для діагностики) */
        engine: engineRef.current,
        status,
        error: errorKind ? SCANNER_ERRORS[errorKind] : null,
        errorKind,
        torch,
        toggleTorch,
    };
};

export default useBarcodeScanner;
